import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setup } from 'additiv-services';
import { Loader } from 'ui-library';
import OIDCAuthenticationProvider, { withSecure } from 'libs/additiv-react-oidc-client';
import { authenticator } from 'libs/additiv-react-oidc-client/core/authenticator';
import { setMemberTokenResolver } from 'additiv-services/services/core/security';
import { useNavigate } from 'react-router-dom';
import { TokenTypes } from 'constants/constants';
import { isAuthenticatedSelector } from '../../redux-store/auth/authSelectors';
import { getUser } from '../../redux-store/auth/authActions';
import { INIT_LOCATION } from '../../constants/sessionStorageKeys';
import getIdentityServerConfig from '../../configs/identity-config';

const SecureLoader = withSecure(Loader);

const AuthenticationProvider = ({ children }) => {
    const navigate = useNavigate();
    const isAuthenticated = useSelector(isAuthenticatedSelector);

    const [isUserLoading, setUserLoading] = useState(!isAuthenticated);
    const dispatch = useDispatch();

    useEffect(() => {
        const initLocation = sessionStorage.getItem(INIT_LOCATION);

        if (!isAuthenticated && !initLocation && !global.location.pathname.startsWith('/auth')) {
            sessionStorage.setItem(INIT_LOCATION, global.location.pathname);
        }
    }, []);

    // Let the services layer refresh the *user* access token instead of falling back to a
    // client_credentials token when the cached member token is close to expiry.
    useEffect(() => {
        setMemberTokenResolver(() => authenticator.getValidAccessToken());

        return () => setMemberTokenResolver(null);
    }, []);

    const getUserData = async (oidcUser) => {
        if (isAuthenticated) { return; }

        setUserLoading(true);
        await dispatch(getUser({
            profileId: oidcUser?.profile?.mid,
        }));
        const initLocation = sessionStorage.getItem(INIT_LOCATION);

        setUserLoading(false);

        navigate(initLocation || '/dashboard');
    };

    const updateToken = (oidcUser) => {
        setup({
            member_token: `${oidcUser?.access_token}`,
            bearerAccessToken: `Bearer ${oidcUser?.access_token}`,
            bearerApplicationAccessToken: `Bearer ${oidcUser?.access_token}`,
        });

        sessionStorage.setItem(TokenTypes.member_token, `${oidcUser?.access_token}`);
    };

    const onLogin = (oidcUser) => {
        updateToken(oidcUser);
        getUserData(oidcUser);
    };

    const onLogout = async () => {
        sessionStorage.removeItem('redux_sessionstorage_auth');

        if (TokenTypes) {
            Object.values(TokenTypes).forEach(type => {
                sessionStorage.removeItem(type);
            });
        }
    };

    return (
        <OIDCAuthenticationProvider
            configuration={getIdentityServerConfig()}
            loadingComponent={<Loader />}
            onUserLoaded={onLogin}
            onLogout={onLogout}
        >
            {isUserLoading ? <SecureLoader /> : children}
        </OIDCAuthenticationProvider>
    );
};

export default AuthenticationProvider;
